import React, { useState, useEffect } from 'react';
import { Pagination as BootstrapPagination } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    const { t } = useTranslation();
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        // Check if mobile on mount
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            onPageChange(page);
        }
    };

    const renderPageItems = () => {
        // Mobile view: show compact page indicator
        if (isMobile) {
            return (
                <BootstrapPagination.Item active className="page-indicator">
                    {t('pagination.page_of', { current: currentPage, total: totalPages })}
                </BootstrapPagination.Item>
            );
        }

        // Desktop view: smart pagination with max 7 links
        const items = [];
        const maxVisiblePages = 7;

        if (totalPages <= maxVisiblePages) {
            // Show all pages if total is less than or equal to max
            for (let i = 1; i <= totalPages; i++) {
                items.push(
                    <BootstrapPagination.Item
                        key={i}
                        active={i === currentPage}
                        onClick={() => handlePageChange(i)}
                    >
                        {i}
                    </BootstrapPagination.Item>
                );
            }
        } else {
            // Always show first page
            items.push(
                <BootstrapPagination.Item
                    key={1}
                    active={1 === currentPage}
                    onClick={() => handlePageChange(1)}
                >
                    1
                </BootstrapPagination.Item>
            );

            // Show ellipsis or pages near current page
            if (currentPage <= 4) {
                // Near start: show 1, 2, 3, 4, 5, ..., last
                for (let i = 2; i <= 5; i++) {
                    items.push(
                        <BootstrapPagination.Item
                            key={i}
                            active={i === currentPage}
                            onClick={() => handlePageChange(i)}
                        >
                            {i}
                        </BootstrapPagination.Item>
                    );
                }
                items.push(<BootstrapPagination.Ellipsis key="ellipsis-end" disabled />);
            } else if (currentPage >= totalPages - 3) {
                // Near end: show 1, ..., last-4, last-3, last-2, last-1, last
                items.push(<BootstrapPagination.Ellipsis key="ellipsis-start" disabled />);
                for (let i = totalPages - 4; i < totalPages; i++) {
                    items.push(
                        <BootstrapPagination.Item
                            key={i}
                            active={i === currentPage}
                            onClick={() => handlePageChange(i)}
                        >
                            {i}
                        </BootstrapPagination.Item>
                    );
                }
            } else {
                // Middle: show 1, ..., current-1, current, current+1, ..., last
                items.push(<BootstrapPagination.Ellipsis key="ellipsis-start" disabled />);
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    items.push(
                        <BootstrapPagination.Item
                            key={i}
                            active={i === currentPage}
                            onClick={() => handlePageChange(i)}
                        >
                            {i}
                        </BootstrapPagination.Item>
                    );
                }
                items.push(<BootstrapPagination.Ellipsis key="ellipsis-end" disabled />);
            }

            // Always show last page
            items.push(
                <BootstrapPagination.Item
                    key={totalPages}
                    active={totalPages === currentPage}
                    onClick={() => handlePageChange(totalPages)}
                >
                    {totalPages}
                </BootstrapPagination.Item>
            );
        }

        return items;
    };

    if (totalPages === 0) {
        return null;
    }

    return (
        <BootstrapPagination>
            <BootstrapPagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
            >
                {t('pagination.prev')}
            </BootstrapPagination.Prev>
            {renderPageItems()}
            <BootstrapPagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
            >
                {t('pagination.next')}
            </BootstrapPagination.Next>
        </BootstrapPagination>
    );
};

export default Pagination;
